import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import ContactUs from "@/assets/icons/contactUs.svg";
import ProfileArrow from "@/assets/icons/arrow.svg";
import DropDown from "@/assets/icons/dropdown-menu.svg";
import { useRouter } from "next/router";
import styles from "./Header.module.scss";
import Link from "next/link";
import AppButton from "@/components/buttons/AppButton/AppButton";
import { useConfig } from "@/context/ConfigContext";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/redux/store";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { useSession, signOut } from "next-auth/react";
import UserIcon from "@/components/icons/User";
import LogoutIcon from "@/components/icons/LogoutIcon";
import UsersIcon from "@/components/icons/UsersIcon";
import SettingsIcon from "@/components/icons/Settings";
import AppDrawer from "@/components/Drawers/AppDrawer/AppDrawer";
import { getUserProfile } from "@/utils/user/getUser";
import fetchAgencyProfile from "@/utils/agency/profile";
import Whatsapp from "@/components/icons/Whatsapp";
import { UserData } from "@/types/User";
import { Avatar } from "@mui/material";
import BuildingIcon from "@/components/icons/BuildingIcon/BuildingIcon";
import EmailIcon from "@/components/icons/Email";

interface HeaderProps {
  logoUrl: string;
  auth?: boolean;
  onSupportClicked: () => void;
}

interface ProfileOption {
  label: string;
  value: string;
  link?: string;
  section?: string;
  className: string;
  icon: JSX.Element;
}

const Header: React.FC<HeaderProps> = ({ logoUrl, auth, onSupportClicked }) => {
  const router = useRouter();
  const { config } = useConfig();
  const { data: session, status } = useSession();
  const dispatch: AppDispatch = useDispatch();

  const isLoginPage = router.pathname === "/signin";
  const isRegisterPage = router.pathname === "/signup";
  const showDevIndicator = process.env.NEXT_PUBLIC_DEV_INDICATOR === "true";
  const contactNumber = config?.config?.support?.phone;

  const [profileData, setProfileData] = useState<UserData | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const fetchUserProfile = async () => {
    try {
      const profile = await getUserProfile(dispatch);
      setProfileData(profile?.response?.data);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  };

  const fetchAgencyName = async () => {
    try {
      const response = await fetchAgencyProfile(dispatch);
      setAgencyName(response?.data?.agency_name ?? null);
    } catch (error) {
      console.error("Failed to fetch agency profile:", error);
    }
  };

  useEffect(() => {
 
    if (!auth || status !== "authenticated") return;
    fetchUserProfile();
    fetchAgencyName();
  }, [auth, status]);

  const handleLoginClick = () => {
    router.push("/signin");
  };

  const handleSignupClick = () => {
    router.push("/signup");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const onWhatsAppClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    const url = `https://wa.me/${config.phoneNumber}`;
    window.open(url, "_blank");
  };

  const closeProfileMenu = () => {
    setIsProfileOpen(false);
    setIsDropdownOpen(false);
  };

  const closeDropdownMenu = () => {
    setIsDropdownOpen(false);
  };

  const handleSelectedOption = async (value: string) => {
    closeProfileMenu();
    if (value === "sign-out") {
      try {
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        sessionStorage.clear();
        localStorage.clear();
        await signOut({ redirect: false });
        const currentDate = new Date();
        const tomorrowDate = new Date();
        tomorrowDate.setDate(currentDate.getDate() + 1);

        dispatch(
          setSearchValues({
            checkInDate: currentDate.toISOString().slice(0, 10),
            checkOutDate: tomorrowDate.toISOString().slice(0, 10),
            nights: 1,
            currency: null,
            ratings: [],
            destination: {
              isProperty: false,
              label: "",
              value: "",
              countryCode: "",
              country: "",
              city: "",
            },
            nationality: null,
            rooms: [{ adults: 1, children: [] }],
          }),
        );

        router.replace("/signin");
      } catch (error) {
        console.error("Error signing out", error);
      }
    } else if (value === "profile") {
      router.push("/profile");
    } else if (value === "account-settings") {
      router.push("/account/settings");
    } else if (value === "users") {
      router.push("/admin/users");
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      profileMenuRef.current &&
      !profileMenuRef.current.contains(event.target as Node)
    ) {
      closeProfileMenu();
    }
    if (
      dropdownMenuRef.current &&
      !dropdownMenuRef.current.contains(event.target as Node)
    ) {
      closeDropdownMenu();
    }
  };

  useEffect(() => {
    if (isProfileOpen || isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen, isDropdownOpen]);

  const profileOptions: ProfileOption[] = [
    {
      label: "My Profile",
      value: "profile",
      link: "/profile",
      className: `${styles.userLabel} ${styles.labelFonts} py-3 hover:bg-slate-100`,
      icon: <UserIcon />,
    },
    ...(session?.user?.userRole === "admin"
      ? [
          {
            label: "Users",
            value: "users",
            section: "Admin",
            link: "/admin/users",
            className: `${styles.userLabel} ${styles.labelFonts} py-3  hover:bg-slate-100`,
            icon: <UsersIcon />,
          },
        ]
      : []),
    {
      label: "Account Settings",
      value: "account-settings",
      ...(session?.user?.userRole === "admin" ? { section: "Admin" } : {}),
      link: "/account/settings",
      className: `${styles.userLabel} ${styles.labelFonts} py-3 hover:bg-slate-100`,
      icon: <SettingsIcon />,
    },
    {
      label: "Sign Out",
      value: "sign-out",
      section: "no-label-section",
      className: `${styles.userLabel} ${styles.labelFonts} py-3 hover:bg-slate-100 rounded-b-lg`,
      icon: <LogoutIcon />,
    },
  ];

  useEffect(() => {
    const getScrollbarWidth = () => {
      const container = document.createElement("div");
      document.body.appendChild(container);
      container.style.overflow = "scroll";

      const inner = document.createElement("div");
      container.appendChild(inner);

      const scrollbarWidth = container.offsetWidth - inner.offsetWidth;
      container.remove();

      return scrollbarWidth;
    };

    const adjustHeaderWidth = () => {
      const scrollbarWidth = getScrollbarWidth();

      if (headerRef.current) {
        headerRef.current.style.width =
          scrollbarWidth > 0 ? `calc(100% - ${scrollbarWidth}px)` : "100%";
      }
    };

    adjustHeaderWidth();
    window.addEventListener("resize", adjustHeaderWidth);

    return () => {
      window.removeEventListener("resize", adjustHeaderWidth);
    };
  }, []);

  const hideSignUpButton = ["/signup"];

  const renderOptions = () => {
    let renderedOptions: JSX.Element[] = [];
    let currentSection: string | undefined = undefined;

    profileOptions.forEach((option, index) => {
      if (option.section && option.section !== currentSection) {
        if (index !== 0) {
          renderedOptions.push(
            <hr
              key={`divider-${index}`}
              className="md:border-gray-300  border-gray-200 my-2"
            />,
          );
        }

        currentSection = option.section;
      }

      const baseClass = "flex gap-2 items-center text-[#343434]";
      const optionLinkClass = `${option.link ? "cursor-pointer" : "cursor-text"}`;

      const optionClass = option.className
        ? `${option.className} ${baseClass} `
        : `option p-4 ${optionLinkClass}  hover:bg-gray-100`;

      if (option.link) {
        renderedOptions.push(
          <Link
            className={optionClass}
            onClick={closeProfileMenu}
            aria-label={`Navigate to ${option.label}`}
            key={option.value}
            href={option.link}
            passHref
          >
            {option.icon ? option.icon : null} {option.label}
          </Link>,
        );
      } else {
        renderedOptions.push(
          <button
            key={option.value}
            className={optionClass}
            onClick={() => {
              handleSelectedOption(option.value);
              closeProfileMenu();
            }}
            aria-label={`Select ${option.label}`}
          >
            {option.icon ? option.icon : null} {option.label}
          </button>,
        );
      }
    });

    return renderedOptions;
  };

  const authHeader = (
    <>
      <nav className="hidden lg:flex items-center justify-between col-start-5 col-end-13 space-x-9 z-50">
        <button onClick={(e) => onWhatsAppClick(e)} className="space-x-2">
          <Whatsapp />
        </button>
        <div className="flex items-center space-x-[4px] ">
          {ContactUs && (
            <Image
              src={ContactUs}
              alt="Contact us image"
              width={12}
              height={14}
            />
          )}
          <p className={`${styles.contactus} text-xs`}>{contactNumber}</p>
        </div>
        <Link passHref href="/home" className={styles.navstyle}>
          Home
        </Link>
          <Link passHref href="/invoices" className={styles.navstyle}>
            Invoices
          </Link>
     
        <Link passHref href="/bookings" className={styles.navstyle}>
          Bookings
        </Link>
        <button
          aria-label="Control support drawer"
          className={`${styles.navstyle} cursor-pointer outline-none`}
          onClick={() => onSupportClicked()}
        >
          Support
        </button>

        <div
          className="relative flex items-center space-x-2 justify-self-end  max-w-max z-50"
          ref={profileMenuRef}
        >
          <button
            className="flex items-center focus:outline-none gap-2"
            onClick={toggleProfileMenu}
          >
            <Avatar
              sx={{
                bgcolor: "#0b559f",
                color: "white",
                width:38,
                height:38,
                fontSize: "14px",
                fontWeight: 600,
                fontFamily:"inherit",
                letterSpacing:"1px"
              }}
            >
              {profileData?.first_name?.[0]}
              {profileData?.last_name?.[0]}
            </Avatar>
           
          </button>
          {isProfileOpen && (
            <div className="animate-dropdownIn origin-top-right absolute right-0 top-10 mt-2 w-[310px] py-4  bg-white shadow-lg rounded-lg z-50">
              <div className="flex flex-col gap-3 px-4 ">
                <div className="flex items-center gap-3">
             
                  <div className="flex flex-col min-w-0">
                    <span className="text-base font-semibold text-[#333333] truncate">
                      {profileData
                        ? `${profileData?.first_name} ${profileData?.last_name}`
                        : session?.user?.name}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 rounded-lg bg-[#F8FAFC] px-3 py-2">
                    <span className="flex items-center gap-1 text-xs text-[#939dad] truncate">
                      <EmailIcon color="#939dad" />
                      <span className="truncate">
                        {profileData ? profileData?.email : session?.user?.email}
                      </span>
                    </span>
                  <div className="flex items-center gap-2">
                    <BuildingIcon />
                    <span className="text-sm font-medium text-[#343434] truncate">
                      {agencyName}
                    </span>
                  </div>
                  <div className="flex gap-2 items-center w-fit px-2 py-1 rounded-full bg-[#EAF2FB] ">
                  <span className="flex items-center [&>svg]:w-3 [&>svg]:h-3 [&_path]:fill-[#0b559f]">
                      <UserIcon />
                  </span>
                  <span className="w-fit flex items-center gap-1 text-xs font-semibold text-[#0b559f]   capitalize">

                    {session?.user?.user_type ?? profileData?.user_type_name}
                  </span>
                  </div>
                </div>
              </div>
              <hr className="!border-gray-200 my-3" />
              <div className="options-container rounded-lg bg-white flex flex-col">
                {renderOptions()}
              </div>
            </div>
          )}
        </div>
      </nav>
      <div
        className="flex lg:hidden items-center relative z-50"
        ref={dropdownMenuRef}
      >
        <button onClick={toggleDropdown}>
          {DropDown && (
            <Image src={DropDown} alt="Dropdown menu" width={20} height={20} />
          )}
        </button>
        <AppDrawer
          show={isDropdownOpen}
          onClose={() => {
            setIsDropdownOpen(!isDropdownOpen);
            closeProfileMenu();
          }}
          title=" "
        >
          <div className=" flex flex-col  mt-2  py-4  bg-white  z-50">
            <div className="flex flex-col items-start gap-1 px-4  pb-3  ">
              <span className="self-stretch text-base font-semibold  text-[#333333] ">
                {profileData
                  ? `${profileData?.first_name} ${profileData?.last_name} `
                  : session?.user?.name}
              </span>
              <div className="flex gap-[6px] items-center self-stretch flex-wrap">
                <span className=" text-sm font-medium  text-[#343434] capitalize ">
                  {session?.user?.user_type ?? profileData?.user_type_name}
                </span>
                <span className=" text-sm font-medium  text-[#939dad] ">
                  {profileData ? profileData?.email : session?.user?.email}
                </span>
              </div>
            </div>
            <hr className="!border-gray-200  my-2 font-normal" />
            <Link passHref href="/home" className={`${styles.navstyle} mt-2`}>
              Home
            </Link>
              <Link passHref href="/invoices" className={styles.navstyle}>
                Invoices
              </Link>
            
            <Link passHref href="/bookings" className={styles.navstyle}>
              Bookings
            </Link>
            <button
              aria-label="Control support drawer"
              className={`${styles.navstyle} cursor-pointer outline-none text-start mb-2`}
              onClick={() => onSupportClicked()}
            >
              Support
            </button>
            <button
              className={`${styles.navstyle} cursor-pointer outline-none text-start mb-2`}
              onClick={(e) => onWhatsAppClick(e)}
            >
              <Whatsapp />
            </button>
            <hr className="!border-gray-200  my-2 font-normal" />

            <div className="options-container mt-2 rounded-lg bg-white flex flex-col">
              {renderOptions()}
            </div>
          </div>
        </AppDrawer>
      </div>
    </>
  );

  return (
    !isLoginPage && (
      <div
        ref={headerRef}
        className={`fixed ${showDevIndicator ? "top-10" : "top-0"} left-0 w-full bg-white border-b-2 border-slate-100 z-20`}
      >
        <header
          className={`flex ${isRegisterPage ? "max-w-7xl px-4 md:flex hidden " : "max-w-6xl"} h-20 justify-between items-center py-5 font-semibold text-slate-600 md:mx-auto mx-4 z-50`}
        >
          <div className="flex items-center col-span-4">
            <button
              onClick={() => router.push("/home")}
              onKeyDown={(e) => e.key === "Enter" && router.push("/home")}
              className="focus:outline-none"
              aria-label="Go to home"
            >
              <img
                src={logoUrl}
                alt="Company logo"
                className="md:h-14 w-auto cursor-pointer h-10"
              />
            </button>
          </div>
          {auth && session ? (
            authHeader
          ) : (
            <div className="flex items-end justify-self-end gap-x-4 col-start-11 col-end-13">
              <AppButton
                variant={isRegisterPage ? "primary" : "secondary"}
                label="Sign in"
                className="md:w-32 w-[100px] h-[40px]"
                onClick={handleLoginClick}
              />

              {!hideSignUpButton.includes(router.pathname) && (
                <AppButton
                  variant="primary"
                  className="md:w-32 w-[100px] h-[40px]"
                  label="Sign up"
                  onClick={handleSignupClick}
                />
              )}
            </div>
          )}
        </header>
      </div>
    )
  );
};

export default Header;
